import { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  gridClasses,
} from "@mui/x-data-grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useOktaAuth } from "@okta/okta-react";
import { alpha, styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import * as XLSX from "xlsx";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import { useNotificationAPI } from "../../as2-onboarding-component/hooks/useClient";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const SuppliersInitiativeSelectionComponent = ({ suppliers, setSuppliers }) => {
  const ODD_OPACITY = 0.2;
  const { oktaAuth } = useOktaAuth();
  const notificationApi = useNotificationAPI(oktaAuth.getAccessToken());
  const noTpData = "No data Available. Please upload a file"
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState(rows);
  const [searchValue, setSearchValue] = useState(null);

  const columns = [
    {
      headerName: "Supplier Number",
      field: "supplier",
      width: 150,
    },
    {
      headerName: "User ID",
      field: "user_id",
      width: 150,
    },
    {
      headerName: "User Email",
      field: "user_email",
      width: 250,
    },
    {
      headerName: "Supplier Qualifier",
      field: "supplier_qualifier",
      width: 150,
    },
    {
      headerName: "Supplier ISA",
      field: "supplier_isa",
      width: 150,
    },
    {
      headerName: "Supplier GS",
      field: "supplier_gs",
      width: 150,

    }

  ];

  const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: theme.palette.grey[100],
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
      "&.Mui-selected": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity
        ),
        "&:hover, &.Mui-hovered": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY +
              theme.palette.action.selectedOpacity +
              theme.palette.action.hoverOpacity
          ),
          "@media (hover: none)": {
            backgroundColor: alpha(
              theme.palette.primary.main,
              ODD_OPACITY + theme.palette.action.selectedOpacity
            ),
          },
        },
      },
    },
  }));

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </GridToolbarContainer>
    );
  };


  const normalizeKey = (key) =>
      key.toLowerCase().replace(/[^a-z0-9]/gi, "").trim();


  const handleFileUpload = (event) => {
    setSearchValue(null);
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    // check file's
    if (!allowedTypes.includes(file.type)) {
      alert("Please upload a valid Excel file (.xls or .xlsx).");
      return;
    }

    const reader = new FileReader();
    reader.readAsBinaryString(file);

    reader.onloadend = (e) => {
      const binaryStr = e.target.result;
      if (!binaryStr) {
        console.error("Failed to read file!");
        return;
      }

      try {
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // check if the excel file is empty
        if (jsonData.length === 0) {
          alert("The uploaded Excel file is empty.");
          return;
        }

        //Normalize keys of first object(every object have the same keys)
        const firstRowKeys = Object.keys(jsonData[0]).map(normalizeKey);
        const requiredKeys = ["suppliernumber", "userid", "useremail"];
        const optionalKeys = ["supplierqualifier","supplierisa", "suppliergs"];

        //Validate object coming from xlsx contains required keys
        const missingHeaders = requiredKeys.filter((key) => !firstRowKeys.includes(key));

        if (missingHeaders.length > 0) {
          const error = `Missing required headers: ${missingHeaders.join(", ")} - Found ${firstRowKeys.join(", ")}. Please upload a new .xlsx file with the correct headers.`;
          console.warn(error);
          alert(error);
          return;
        }

        const suppliersData = jsonData.map((row, index) => {
          const normalizedRow = {};
          Object.keys(row).forEach((key) => {
            normalizedRow[normalizeKey(key)] = row[key];
          });

          //Generate contact object
          return {
            id: index + 1,
            supplier: String(normalizedRow["suppliernumber"]).padStart(10, "0"),
            user_id: normalizedRow["userid"],
            user_email: normalizedRow["useremail"],
            supplier_qualifier: normalizedRow["supplierqualifier"] || null,
            supplier_isa: normalizedRow["supplierisa"] || null,
            supplier_gs: normalizedRow["suppliergs"] || null,

          };
        });

        setRows(suppliersData);
        setFilteredRows(suppliersData);

        const newFormattedData = suppliersData.map((row) => ({
          supplier_number: row.supplier,
          supplier_name: row.user_id,
          supplier_email: row.user_email,
          supplier_qualifier: row.supplier_qualifier,
          supplier_isa_id: row.supplier_isa,
          supplier_gs_id: row.supplier_gs,

        }));

        setSuppliers(newFormattedData);
      } catch (error) {
        console.error("Error parsing file:", error);
      }finally {
        event.target.value = null;
      }
    };

    reader.onerror = (error) => {
      console.error("Error reading file:", error);
    };
  };

  const checkSuppliers = () => {
    if (suppliers.length > 0) {
      suppliers.map(async (supplier) => {
        try {
          const response = await notificationApi.get(
              `/suppliers/search-by-number?supplier_number=${supplier.supplier_number}`
          );
          const data = response.data;

          if (data.length === 0) {
            // FGIV1AI - If new supplier, create it
            await handleSupplierCreation(supplier);
          } else {
            // FGIV1AI - Verify existing supplier and update if necessary
            const existingSupplier = data.find((item) =>
                item.supplier_email === supplier.supplier_email && item.supplier_name === supplier.supplier_name
            );

            if (areSupplierDetailsEqual(existingSupplier, supplier)) {
              console.log("Supplier already exists with the same details, skipping creation:", supplier.supplier_number);
              return;
            }

            await handleSupplierCreation(supplier, true);
          }
        } catch (error) {
          console.error("Error fetching suppliers:", error);
        }
      });
    }
  };

  /**
   * Creates or updates a supplier based on the provided data.
   */
  async function handleSupplierCreation(supplier, isUpdate = false) {
    try {
      const response = await notificationApi.post("/suppliers", supplier);
      if (response.status === 201) {
        console.log(
            isUpdate ? "Supplier updated successfully:" : "Supplier added successfully:",
            supplier
        );
      } else {
        console.error(
            isUpdate ? "Failed to update supplier:" : "Failed to add supplier:",
            supplier
        );
      }
    } catch (error) {
      console.error("Error handling supplier:", error);
    }
  }

  /**
   * Compares the details of two suppliers and checks if they are the same.
   */
  function areSupplierDetailsEqual(existing, newSupplier) {
    const keysToCompare = ["supplier_number", "supplier_isa_id", "supplier_gs_id", "supplier_qualifier"];
    return keysToCompare.every(key => existing[key] === newSupplier[key]);
  }

  useEffect(() => {
    checkSuppliers();
  }, [suppliers]);

  const handleSelection = (event, newValue) => {
    setSearchValue(newValue);
    if (newValue) {
      const { type, code } = newValue;
      const newFilteredRows = rows.filter((row) => {
        if (type === "Supplier")
          return String(row.supplier).toLowerCase() === code.toLowerCase();
        if (type === "User ID")
          return String(row.user_id).toLowerCase() === code.toLowerCase();
        if (type === "User Email")
          return String(row.user_email).toLowerCase() === code.toLowerCase();
        return false;
      });
      setFilteredRows(newFilteredRows);
    } else {
      setFilteredRows(rows);
    }
  };

  return (
    <Box>
      <Typography gutterBottom variant="h3" color="grey">
        Add the information for the suppliers
      </Typography>
      <Grid container sx={{alignItems:"flex-start"}} spacing={8} pt={5}>
       {/*  <Grid item xs={6}>
          <Autocomplete
            size="small"
            popupIcon={null}
            options={suppliers}
            value={searchValue}
            onChange={handleSelection}
            groupBy={(option) => option.type.replace("_", " ")}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for Suppliers, UserID or UserEmail"
                size="small"
              />
            )}
          />
        </Grid> */}

        <Grid item size={{sx:3}}>
          <Box>
            <Button
              sx={{ color: "#528b47", borderColor: "#528b47" }}
              component="label"
              role={undefined}
              variant="secondary"
              startIcon={<CloudUploadIcon />}
            >
              Upload files
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileUpload}
                accept=".xlsx, .xls"
              />
            </Button>
          </Box>
        </Grid>
      </Grid>
      <div style={{ height: "50vh", width: "100%", paddingTop: "10px" }}>
        <StripedDataGrid
          rows={filteredRows}
          columns={columns}
          density="compact"
          autoHeight={false}
          pageSize={100}
          filterOptions={(x) => x}
          sx={{
            borderColor: "rgb(204, 204, 204)",
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          getCellClassName={(params) => {
            return "cell";
          }}
          components={{
            Toolbar: CustomToolbar,
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <div style={{ fontWeight: "bold", color: "grey" }}>
                  <FolderIcon /> {noTpData}
                </div>
              </Stack>
            ),
          }}
        />
      </div>
    </Box>
  );
};

export default SuppliersInitiativeSelectionComponent;
